import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Boxes, IndianRupee, TrendingUp, CalendarClock, Plug } from "lucide-react";
import api, { inr, fmtDate } from "../../lib/api";
import StatusBadge from "../../components/StatusBadge";
import { FullScreenLoader } from "../../context/AuthContext";

export default function AdminDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <FullScreenLoader label="Loading control tower" />;
  const s = data.stats;

  const cards = [
    { label: "Customers", value: s.total_customers, icon: Users, mono: false },
    { label: "Total Shipments", value: s.total_shipments, icon: Boxes, mono: false },
    { label: "Shipments Today", value: s.today_shipments, icon: CalendarClock, mono: false },
    { label: "Revenue", value: inr(s.revenue), icon: IndianRupee, mono: true },
    { label: "Provider Cost", value: inr(s.provider_cost), icon: IndianRupee, mono: true },
    { label: "Gross Profit", value: inr(s.gross_profit), icon: TrendingUp, mono: true, accent: true },
    { label: "Margin", value: s.margin_pct + "%", icon: TrendingUp, mono: true },
  ];

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-shipany-navy">Control Tower</h1>
        <p className="text-sm text-slate-500">Platform health, revenue and network status.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7" data-testid="admin-stats">
        {cards.map((c) => (
          <div key={c.label} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`admin-stat-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <c.icon className={`h-4 w-4 ${c.accent ? "text-emerald-500" : "text-shipany-blue"}`} />
            <p className={`mt-3 text-xl font-bold ${c.mono ? "mono" : "font-display"} ${c.accent ? "text-emerald-600" : "text-shipany-navy"}`}>{c.value}</p>
            <p className="text-xs font-medium text-slate-500">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        <div className="rounded-2xl bg-shipany-navy p-6 text-white lg:col-span-1" data-testid="provider-health-panel">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-lg font-bold">Provider Health</h2>
            <Plug className="h-4 w-4 text-shipany-cyan" />
          </div>
          <div className="mt-4 space-y-3">
            {data.providers.map((p) => (
              <div key={p.code} className="flex items-center justify-between rounded-xl bg-white/5 px-4 py-3" data-testid={`provider-health-${p.code}`}>
                <div>
                  <p className="text-sm font-semibold">{p.name}</p>
                  <p className="mono text-[10px] text-slate-400">{p.courier_count} couriers · P{p.priority}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-flex items-center gap-1.5 text-xs font-bold ${p.enabled ? "text-emerald-400" : "text-slate-500"}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${p.enabled ? "bg-emerald-400 pulse-dot" : "bg-slate-500"}`} />
                    {p.enabled ? "LIVE" : "OFF"}
                  </span>
                  <p className="mono text-[10px] text-shipany-cyan">{p.health?.latency_ms ? `${p.health.latency_ms}ms` : "—"}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white lg:col-span-2" data-testid="admin-recent-shipments">
          <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
            <h2 className="font-display text-lg font-bold text-shipany-navy">Latest Shipments</h2>
            <Link to="/admin/shipments" className="text-sm font-semibold text-shipany-blue hover:underline">All</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-6 py-3">AWB</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Price</th><th className="px-4 py-3">Status</th></tr></thead>
              <tbody>
                {data.recent_shipments.map((sh) => (
                  <tr key={sh.shipment_id} className="border-t border-slate-100">
                    <td className="mono px-6 py-3 font-semibold text-shipany-blue">{sh.awb}</td>
                    <td className="px-4 py-3 text-slate-500">{sh.customer_email}</td>
                    <td className="mono px-4 py-3 font-semibold">{inr(sh.price)}</td>
                    <td className="px-4 py-3"><StatusBadge status={sh.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="status-distribution">
          <h2 className="font-display text-lg font-bold text-shipany-navy">Status Mix</h2>
          <div className="mt-4 space-y-2.5">
            {Object.entries(data.status_distribution).map(([st, n]) => (
              <div key={st} className="flex items-center justify-between text-sm">
                <StatusBadge status={st} />
                <span className="mono font-bold text-shipany-navy">{n}</span>
              </div>
            ))}
            {Object.keys(data.status_distribution).length === 0 && <p className="text-sm text-slate-400">No shipments yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
